<template>
  <div>
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title> Master Calendar </v-toolbar-title>
      <v-spacer></v-spacer>
    </v-app-bar>
    <div class="ma-4" id="calendar-master">
      <v-container>
        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col cols="12" md="6">
                <full-calendar
                  ref="fullcalendar"
                  :options="options"
                ></full-calendar>
                <small>Timezone: {{ calendarOptions.timeZone }}</small>
                <div class="my-5" v-if="eventSelectedDetails.title">
                  <v-row no-gutters justify="space-between" align="center">
                    <v-col cols="4"><h4>Event Details</h4></v-col>
                  </v-row>

                  <hr />
                  <h3 class="mt-5">
                    <v-icon color="grey darken-2"> mdi-account-group </v-icon>
                    <span class="ml-4">{{
                      eventSelectedDetails.extendedProps.group_name
                    }}</span>
                  </h3>
                  <h3 class="primary--text mt-5">
                    {{ eventSelectedDetails.title }}
                  </h3>
                  <p>
                    <small
                      >Date:
                      <strong
                        >{{
                          eventSelectedDetails.startRecur
                            ? formatDate(eventSelectedDetails.startRecur)
                            : formatDate(eventSelectedDetails.start)
                        }}
                        <span
                          v-if="
                            eventSelectedDetails.end &&
                              eventSelectedDetails.isRecurring
                          "
                          >- {{ formatDate(eventSelectedDetails.end) }}</span
                        ></strong
                      ></small
                    ><br />
                    <small
                      >Time:
                      <strong
                        ><span v-if="eventSelectedDetails.allDay">All day</span
                        ><span v-else
                          >{{
                            formatTime(
                              new Date(
                                eventSelectedDetails.startRecur ||
                                  eventSelectedDetails.start
                              ).getTime()
                            )
                          }}
                          -
                          {{
                            formatTime(
                              new Date(
                                eventSelectedDetails.endRecur ||
                                  eventSelectedDetails.end
                              ).getTime()
                            )
                          }}</span
                        ></strong
                      ></small
                    ><br />
                    <span v-if="eventSelectedDetails.isRecurring">
                      <small
                        >Every:
                        <strong
                          v-if="
                            eventSelectedDetails.extendedProps.repeat_by ===
                              `daily`
                          "
                          >Daily</strong
                        >
                        <strong
                          v-else
                          v-for="(repeat, i) in eventSelectedDetails.repeats"
                          :key="`repeat-` + i"
                          >&nbsp;{{ repeat
                          }}<span
                            v-if="i <= eventSelectedDetails.repeats.length - 2"
                            >,</span
                          >
                        </strong></small
                      >
                    </span>
                  </p>

                  <p>
                    {{ eventSelectedDetails.extendedProps.description }}
                  </p>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <v-simple-table>
                  <thead>
                    <tr>
                      <th width="50%" class="text-left">Group</th>
                      <th width="50%" class="text-left">Title</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template
                      v-if="
                        eventsForADay !== undefined && eventsForADay.length > 0
                      "
                    >
                      <tr
                        v-for="eventForADay in eventsForADay"
                        :key="eventForADay.id"
                        @click="selectEvent(eventForADay)"
                      >
                        <td>
                          {{ eventForADay.extendedProps.group_name }}
                        </td>
                        <td>
                          {{ eventForADay.title }}
                        </td>
                      </tr>
                    </template>
                    <tr v-else>
                      <td
                        class="text-center headline grey--text py-10"
                        colspan="6"
                      >
                        No reports available.
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
            <v-overlay absolute :value="isLoading" opacity="0.1">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>
            </v-overlay>
            <v-snackbar
              v-model="snackbar.show"
              :color="snackbar.status"
              :vertical="true"
              right
            >
              {{ snackbar.message }}
              <template v-slot:action="{ attrs }">
                <v-btn
                  text
                  color="white"
                  v-bind="attrs"
                  @click="snackbar.show = false"
                >
                  Ok
                </v-btn>
              </template>
            </v-snackbar>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script>
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import dayjs from 'dayjs'
import { mapState, mapMutations, mapActions } from 'vuex'

const isBetween = require('dayjs/plugin/isBetween')
dayjs.extend(isBetween)

export default {
  name: 'Calendar',
  components: {
    AppBarNavIcon,
    FullCalendar
  },
  data() {
    return {
      isLoading: false,
      snackbar: {
        show: false,
        message: null,
        status: ''
      },
      selectedDay: null,
      eventsForADay: [],
      eventSelected: {},
      options: {
        plugins: [dayGridPlugin, interactionPlugin],
        eventClick: async event => {
          this.isLoading = true
          this.eventSelected.id = parseInt(event.event._def.publicId)
          await this.getEvent(this.eventSelected.id)
          this.isLoading = !this.eventSelectedDetails.title
        },
        dateClick: event => {
          if (this.selectedDay) {
            this.selectedDay.dayEl.classList.remove('selected-date')
          }
          event.dayEl.classList.add('selected-date')
          this.selectedDay = event
        }
      }
    }
  },
  async mounted() {
    this.isLoading = true
    await this.setTimezone(dayjs.tz.guess())
    await this.refreshCalendar()
    this.isLoading = false
  },
  watch: {
    time: {
      deep: true,
      handler() {
        this.updateEndTime()
        this.updateFormEventDate()
      }
    },
    date: {
      deep: true,
      handler() {
        this.updateEndDate()
        this.updateEndTime()
        this.updateFormEventDate()
      }
    },
    selectedDay: {
      deep: true,
      handler() {
        if (this.options.events) {
          this.eventsForADay = this.options.events.filter(event => {
            if (
              !event.isRecurring &&
              !dayjs(event.start).isSame(this.selectedDay.dateStr, 'day')
            )
              return false

            if (
              event.daysOfWeek &&
              !event.daysOfWeek.includes(this.selectedDay.date.getDay())
            )
              return false

            const startDate = event.startRecur
              ? dayjs(event.startRecur)
              : dayjs(event.start)
            const endDate = event.endRecur
              ? dayjs(event.endRecur)
              : dayjs(event.end)

            return dayjs(this.selectedDay.dateStr).isBetween(
              startDate,
              endDate,
              'day',
              '[]'
            )
          })
        }
      }
    }
  },
  computed: {
    ...mapState({
      calendarOptions: state => state.calendar.options,
      httpError: state => state.calendar.httpError,
      eventSelectedDetails: state => state.calendar.eventSelectedDetails
    }),
    minStartDate() {
      return dayjs().format('YYYY-MM-DD')
    }
  },
  methods: {
    ...mapActions({
      getEvent: 'calendar/getEvent',
      getEvents: 'calendar/getEvents'
    }),
    ...mapMutations({
      setTimezone: 'calendar/setTimezone',
      clearEventSelectedDetails: 'calendar/clearEventSelectedDetails'
    }),

    updateDate(value, date) {
      date.value = value
    },
    updateTime(value, time) {
      time.value = value
    },

    async refreshCalendar() {
      await this.getEvents({ 'filter[type]': 'group', include: 'group' }).then(
        () => {
          this.options = { ...this.calendarOptions, ...this.options }
          this.options.events = this.calendarOptions.events
        }
      )
    },
    formatDate(date) {
      return dayjs(date)
        .tz(this.calendarOptions.timeZone)
        .format('MMMM D')
    },
    formatTime(time) {
      return dayjs(time)
        .tz(this.calendarOptions.timeZone)
        .format('h:mm A')
    },

    async selectEvent(event) {
      this.isLoading = true
      this.eventSelected.id = event.id
      await this.getEvent(this.eventSelected.id)
      this.isLoading = !this.eventSelectedDetails.title
    }
  }
}
</script>

<style lang="sass">
#calendar-master
  .col
    padding-top: 0
  .v-card__title.subtitle-1
      padding: 0 !important
  .weekdays
    .v-input--checkbox
      width: 80px
.selected-date
  background-color: #F2FBFC
.text-truncate
  max-width: 175px
.v-data-table__wrapper > table > tbody > tr:hover
  cursor: pointer
</style>
